import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import config from '../../Config'
import { loadFeatureFlagsFromStorage, saveFeatureFlagsToStorage } from '../../shared/libs/featureFlagsPersistence'

export interface FeatureFlags {
  enableGamer: boolean
  enableFacilitator: boolean
  enableLanguageSwitcher: boolean
  enableLatestSessionFeedback: boolean
  enableGenerateReports: boolean
  enableExperimentalTeamFeedback: boolean
  enableAchievements: boolean
}

/** A page asking the floating assistant to open; a new `requestId` makes a repeat ask count. */
export interface AssistantPanelRequest {
  requestId: number
}

/** Where the platform tour stands in this visit; a reload forgets a paused tour. */
export enum PlatformTourPhase {
  WELCOME = 'welcome',
  STOP = 'stop',
  PAUSED = 'paused',
  DONE = 'done',
  ENDED = 'ended'
}

export interface PlatformTourState {
  phase: PlatformTourPhase
  stopIndex: number
}

interface UiState {
  featureFlags: FeatureFlags
  assistantPanelRequest?: AssistantPanelRequest
  /** The session whose feedback is on screen right now, so Aria can greet it once. */
  latestFeedbackOnScreenSessionId?: string
  platformTour?: PlatformTourState
}

// Read default flags from Config.ts (environment-aware)
const getDefaultFlags = (): FeatureFlags => {
  const flags = {
    enableGamer: config.enableGamer,
    enableFacilitator: config.enableFacilitator,
    enableLanguageSwitcher: config.enableLanguageSwitcher,
    enableLatestSessionFeedback: config.enableLatestSessionFeedback,
    enableGenerateReports: config.enableGenerateReports,
    enableExperimentalTeamFeedback: config.enableExperimentalTeamFeedback,
    enableAchievements: config.enableAchievements ?? false
  }

  return flags
}

// Load flags from localStorage on initialization
const getInitialFlags = (): FeatureFlags => {
  const defaults = getDefaultFlags()
  const stored = loadFeatureFlagsFromStorage()

  if (stored) {
    // Merge stored flags with defaults (stored takes precedence)
    return {
      ...defaults,
      ...stored
    }
  }

  return defaults
}

const initialState: UiState = {
  featureFlags: getInitialFlags()
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    updateFeatureFlag: (state, action: PayloadAction<{ flag: keyof FeatureFlags; value: boolean }>) => {
      const { flag, value } = action.payload
      state.featureFlags[flag] = value
      // Persist to localStorage
      saveFeatureFlagsToStorage(state.featureFlags)
    },
    updateMultipleFeatureFlags: (state, action: PayloadAction<Partial<FeatureFlags>>) => {
      state.featureFlags = { ...state.featureFlags, ...action.payload }
      // Persist to localStorage
      saveFeatureFlagsToStorage(state.featureFlags)
    },
    resetFeatureFlags: (state) => {
      state.featureFlags = getDefaultFlags()
      // Clear localStorage and save defaults
      saveFeatureFlagsToStorage(state.featureFlags)
    },
    requestAssistantPanel: (state) => {
      state.assistantPanelRequest = { requestId: (state.assistantPanelRequest?.requestId ?? 0) + 1 }
    },
    setLatestFeedbackOnScreen: (state, action: PayloadAction<string | undefined>) => {
      state.latestFeedbackOnScreenSessionId = action.payload
    },
    showPlatformTourWelcome: (state) => {
      state.platformTour = { phase: PlatformTourPhase.WELCOME, stopIndex: 0 }
    },
    showPlatformTourStop: (state, action: PayloadAction<number>) => {
      state.platformTour = { phase: PlatformTourPhase.STOP, stopIndex: action.payload }
    },
    setPlatformTourPhase: (state, action: PayloadAction<PlatformTourPhase>) => {
      state.platformTour = { phase: action.payload, stopIndex: state.platformTour?.stopIndex ?? 0 }
    },
    closePlatformTour: (state) => {
      state.platformTour = undefined
    }
  }
})

export const {
  updateFeatureFlag,
  updateMultipleFeatureFlags,
  resetFeatureFlags,
  requestAssistantPanel,
  setLatestFeedbackOnScreen,
  showPlatformTourWelcome,
  showPlatformTourStop,
  setPlatformTourPhase,
  closePlatformTour
} = uiSlice.actions

export default uiSlice.reducer
